import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const NetworkScanJobContext = createContext(null);

/**
 * Tracks the active network scan job and polls in the background so progress continues
 * when the user navigates away from /network-scan (backend job is unchanged).
 */
export function NetworkScanJobProvider({ children }) {
  const { user } = useAuth();
  const [tracked, setTracked] = useState(null);
  const toastDoneRef = useRef(false);

  useEffect(() => {
    if (!user) {
      setTracked(null);
      toastDoneRef.current = false;
    }
  }, [user]);

  const registerScan = useCallback((p) => {
    toastDoneRef.current = false;
    setTracked({
      jobId: p.jobId,
      customerId: p.customerId,
      scanStartedAt: p.scanStartedAt ?? Date.now(),
      targetsLabel: p.targetsLabel ?? '',
      includeNoPorts: Boolean(p.includeNoPorts),
      job: p.initialJob ?? { job_id: p.jobId, status: 'queued' },
      devices: p.devices ?? null,
    });
  }, []);

  const loadJobForDisplay = useCallback(async (customerId, jobId) => {
    const data = await api.get(`/api/network-scan/${customerId}/jobs/${jobId}`);
    let devices = null;
    if (data.status === 'completed' && data.discovery_scan_id) {
      const d = await api.get(`/api/network-scan/${customerId}/jobs/${jobId}/devices`);
      devices = d.devices || [];
    }
    toastDoneRef.current = true;
    setTracked({
      jobId,
      customerId,
      scanStartedAt: data.created_at ? Date.parse(data.created_at) : null,
      targetsLabel: Array.isArray(data.targets_json) ? data.targets_json.join(', ') : '',
      includeNoPorts: true,
      job: data,
      devices,
    });
  }, []);

  const clearTracked = useCallback(() => {
    setTracked(null);
    toastDoneRef.current = false;
  }, []);

  useEffect(() => {
    if (!tracked?.jobId || !tracked.customerId) return undefined;
    const st = tracked.job?.status;
    if (st === 'completed' || st === 'failed') return undefined;

    let cancelled = false;
    const jid = tracked.jobId;
    const cid = tracked.customerId;

    const tick = async () => {
      try {
        const data = await api.get(`/api/network-scan/${cid}/jobs/${jid}`);
        if (cancelled) return;
        let devices = null;
        if (data.status === 'completed' && data.discovery_scan_id) {
          const d = await api.get(`/api/network-scan/${cid}/jobs/${jid}/devices`);
          devices = d.devices || [];
        }
        setTracked((prev) => {
          if (!prev || prev.jobId !== jid || prev.customerId !== cid) return prev;
          return { ...prev, job: data, devices };
        });
        if (data.status === 'completed' && !toastDoneRef.current) {
          toastDoneRef.current = true;
          toast.success('Network scan completed');
        }
        if (data.status === 'failed' && !toastDoneRef.current) {
          toastDoneRef.current = true;
          toast.error(typeof data.error_text === 'string' ? data.error_text : 'Network scan failed');
        }
      } catch (e) {
        console.error(e);
      }
    };

    const id = setInterval(tick, 1500);
    tick();
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [tracked?.jobId, tracked?.customerId, tracked?.job?.status]);

  const value = useMemo(
    () => ({
      tracked,
      registerScan,
      loadJobForDisplay,
      clearTracked,
    }),
    [tracked, registerScan, loadJobForDisplay, clearTracked]
  );

  return <NetworkScanJobContext.Provider value={value}>{children}</NetworkScanJobContext.Provider>;
}

export function useNetworkScanJob() {
  const ctx = useContext(NetworkScanJobContext);
  if (!ctx) {
    throw new Error('useNetworkScanJob must be used within NetworkScanJobProvider');
  }
  return ctx;
}

/** Returns null if provider is missing (safe for optional UI). */
export function useNetworkScanJobOptional() {
  return useContext(NetworkScanJobContext);
}
